/**
 * RouteFallback — shared Suspense fallback for lazy routes.
 *
 * Replaces the 12 inline `<div className="p-6">Loading…</div>` variants
 * in App.tsx with one centered, brand-aware shell:
 *   - Full viewport height so the page doesn't collapse around a single
 *     line of text (no layout shift when the chunk resolves).
 *   - Subtle teal spinner using existing accent tokens.
 *   - Optional contextual label (`Loading Studio…`) for screen readers
 *     and visible text on slower networks.
 *   - Respects `prefers-reduced-motion` — spinner becomes a static dot.
 *
 * Pure presentation; no props beyond the optional label.
 */

import type { CSSProperties } from 'react';
import { useI18n } from '../i18n/I18nProvider.tsx';

export interface RouteFallbackProps {
  /** Optional contextual label rendered below the spinner. e.g. "Studio". */
  readonly label?: string;
}

const SHELL_STYLE: CSSProperties = {
  minHeight: '60vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  color: 'var(--text-muted)',
};

const SPINNER_STYLE: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '2px solid var(--border-subtle)',
  borderTopColor: 'var(--accent-primary)',
  animation: 'route-fallback-spin 720ms linear infinite',
};

const LABEL_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.02em',
  color: 'var(--text-muted)',
};

export function RouteFallback({ label }: RouteFallbackProps): JSX.Element {
  const { t } = useI18n();
  return (
    <div role="status" aria-live="polite" style={SHELL_STYLE}>
      <span aria-hidden="true" className="route-fallback-spinner" style={SPINNER_STYLE} />
      <span style={LABEL_STYLE}>{label ? t('chrome.loading.named', { label }) : t('chrome.loading')}</span>
    </div>
  );
}
